import { useState, useCallback, useEffect } from "react";
import { findIndex } from "lodash";

const STORAGE_KEY = "kanban-collapsed-columns";

export interface UseKanbanCollapsedColumns {
  collapsedColumnIds: number[];
  isColumnCollapsed: (columnId: number) => boolean;
  toggleColumn: (columnId: number) => void;
  collapseColumn: (columnId: number) => void;
  expandColumn: (columnId: number) => void;
}

export const useKanbanCollapsedColumns = (): UseKanbanCollapsedColumns => {
  const getInitialCollapsedColumns = (): number[] => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return Array.isArray(parsed.collapsedColumnIds)
          ? parsed.collapsedColumnIds
          : [];
      }
      return [];
    } catch (error) {
      console.error(
        "Error accessing localStorage for collapsed columns:",
        error
      );
      return [];
    }
  };

  const [collapsedColumnIds, setCollapsedColumnIdsState] = useState<number[]>(
    getInitialCollapsedColumns
  );

  useEffect(() => {
    try {
      const state = { collapsedColumnIds };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error("Error setting localStorage for collapsed columns:", error);
    }
  }, [collapsedColumnIds]);

  const isColumnCollapsed = useCallback(
    (columnId: number): boolean => {
      return collapsedColumnIds.includes(columnId);
    },
    [collapsedColumnIds]
  );

  const collapseColumn = useCallback((columnId: number): void => {
    setCollapsedColumnIdsState((prevIds) => {
      if (!prevIds.includes(columnId)) {
        return [...prevIds, columnId];
      }
      return prevIds;
    });
  }, []);

  const expandColumn = useCallback((columnId: number): void => {
    setCollapsedColumnIdsState((prevIds) => {
      const columnIndex = findIndex(prevIds, (id) => id === columnId);
      if (columnIndex !== -1) {
        const updatedIds = [...prevIds];
        updatedIds.splice(columnIndex, 1);
        return updatedIds;
      }
      return prevIds;
    });
  }, []);

  const toggleColumn = useCallback(
    (columnId: number): void => {
      if (isColumnCollapsed(columnId)) {
        expandColumn(columnId);
      } else {
        collapseColumn(columnId);
      }
    },
    [isColumnCollapsed, expandColumn, collapseColumn]
  );

  return {
    collapsedColumnIds,
    isColumnCollapsed,
    toggleColumn,
    collapseColumn,
    expandColumn
  };
};
